import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'src/utils/axios';
import type {
  AgentHealth,
  CaseSummary,
} from 'src/components/SupportCase/types';

export interface CaseFilters {
  q: string;
  state: string;
  archived: boolean;
}

interface UseSupportCasesResult {
  cases: CaseSummary[];
  total: number;
  hasMore: boolean;
  isLoading: boolean;
  isLoadingMore: boolean;
  error: string | null;
  filters: CaseFilters;
  setFilters: (next: Partial<CaseFilters>) => void;
  refetch: () => Promise<void>;
  loadMore: () => Promise<void>;
}

const EMPTY_FILTERS: CaseFilters = { q: '', state: '', archived: false };

const PAGE_SIZE = 25;

/**
 * The conversation list, on a keyset cursor (PA-15).
 *
 * Three things this hook is careful about:
 *
 * * **`total` is the server's**, not `cases.length`. The old list printed the page size
 *   as the count, so a user with 101 conversations was told they had 100 and could not
 *   reach the last one.
 * * **Load more appends; it never re-fetches.** The cursor is opaque and comes back with
 *   each page, so a row created while the user reads page one cannot shift page two.
 * * **A filter change resets the cursor.** Keeping it would page into the previous
 *   filter's tail, which is how "search" starts returning rows that do not match.
 */
export const useSupportCases = (enabled = true): UseSupportCasesResult => {
  const [cases, setCases] = useState<CaseSummary[]>([]);
  const [total, setTotal] = useState(0);
  const [cursor, setCursor] = useState('');
  const [hasMore, setHasMore] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [filters, setFiltersState] = useState<CaseFilters>(EMPTY_FILTERS);

  // The request generation, so a slow first page cannot land after a fast second one.
  const generation = useRef(0);

  const params = useCallback(
    (before: string): Record<string, string | number> => {
      const query: Record<string, string | number> = { limit: PAGE_SIZE };
      if (before) {
        query.before = before;
      }
      if (filters.q.trim()) {
        query.q = filters.q.trim();
      }
      if (filters.state) {
        query.state = filters.state;
      }
      if (filters.archived) {
        query.archived = 1;
      }
      return query;
    },
    [filters]
  );

  const load = useCallback(
    async (before: string, mode: 'replace' | 'append'): Promise<void> => {
      const ticket = generation.current + 1;
      generation.current = ticket;

      if (mode === 'replace') {
        setIsLoading(true);
      } else {
        setIsLoadingMore(true);
      }
      try {
        const response = await axios.get('/support/cases/', {
          params: params(before),
        });
        if (generation.current !== ticket) {
          return;
        }
        const page = (response.data.cases || []) as CaseSummary[];
        setCases((current) =>
          mode === 'append' ? [...current, ...page] : page
        );
        setTotal(Number(response.data.total ?? page.length));
        setHasMore(Boolean(response.data.has_more));
        setCursor(String(response.data.next_before || ''));
        setError(null);
      } catch {
        if (generation.current === ticket) {
          setError('We could not load your cases. Try again in a moment.');
        }
      } finally {
        if (generation.current === ticket) {
          setIsLoading(false);
          setIsLoadingMore(false);
        }
      }
    },
    [params]
  );

  const refetch = useCallback(async (): Promise<void> => {
    await load('', 'replace');
  }, [load]);

  const loadMore = useCallback(async (): Promise<void> => {
    if (!cursor || isLoadingMore) {
      return;
    }
    await load(cursor, 'append');
  }, [cursor, isLoadingMore, load]);

  const setFilters = useCallback((next: Partial<CaseFilters>): void => {
    setFiltersState((current) => ({ ...current, ...next }));
  }, []);

  useEffect(() => {
    if (enabled) {
      void load('', 'replace');
    }
  }, [enabled, load]);

  return useMemo(
    () => ({
      cases,
      total,
      hasMore,
      isLoading,
      isLoadingMore,
      error,
      filters,
      setFilters,
      refetch,
      loadMore,
    }),
    [
      cases,
      total,
      hasMore,
      isLoading,
      isLoadingMore,
      error,
      filters,
      setFilters,
      refetch,
      loadMore,
    ]
  );
};

interface UseAgentHealthResult {
  health: AgentHealth | null;
  isLoading: boolean;
}

/**
 * Whether the support agent is reachable (ENG-4137, decision 2A).
 *
 * The answer decides whether the composer is usable at all. Cached server-side for
 * five seconds, so several panels on a page cannot turn into a probe flood.
 */
export const useAgentHealth = (enabled = true): UseAgentHealthResult => {
  const [health, setHealth] = useState<AgentHealth | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (!enabled) {
      return undefined;
    }
    let cancelled = false;
    setIsLoading(true);
    axios
      .get('/support/agent/health/')
      .then((response) => {
        if (!cancelled) {
          setHealth(response.data as AgentHealth);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setHealth(null);
        }
      })
      .finally(() => {
        if (!cancelled) {
          setIsLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [enabled]);

  return { health, isLoading };
};

export default useSupportCases;
